import { Controller } from 'stimulus'
import { useDebounce } from 'stimulus-use'

/**
 * @namespace Flyout
 */
export default class extends Controller {
  static targets = [
    'flyout',
    'content',
    'closeButton',
    'triggerButton',
    'coveredElement'
  ];

  static debounces = [{
    name: 'toggleInteractiveElements',
    wait: 500
  }];

  /**
   * Initialize
   */
  connect() {
    useDebounce(this)
  }

  /**
   * Use a CSS custom property to determine the full height of the viewport more accurately.
   * {@link https://css-tricks.com/the-trick-to-viewport-units-on-mobile/}
   */
  handleSetViewportSize() {
    const vh = $(window).outerHeight() * 0.01
    $(this.flyoutTarget).css('--viewport-height-multiplier', `${vh}px`)
  }

  /**
   * Show the Flyout to users + assistive technologies.
   */
  openFlyout(event) {
    $(this.flyoutTarget).attr('aria-hidden', 'false')
    this.clearTriggerButtonsPressed()
    const trigger = this.triggerButtonTargets.find(button => event.target === button || event.target.closest('button') === button)
    $(trigger).attr('aria-pressed', 'true')
    $(this.flyoutTarget).removeAttr('hidden')
    $(this.closeButtonTarget).focus()
    this.toggleInteractiveElements()
  }

  /**
   * Hide the Flyout from users + assistive technologies.
   */
  closeFlyout() {
    this.clearTriggerButtonsPressed()
    $(this.flyoutTarget).attr({
      'aria-hidden': 'true',
      'data-action-id': '',
      hidden: ''
    })
    this.toggleInteractiveElements()
  }

  /**
   * Determine whether or not Flyout is open.
   */
  flyoutIsOpen() {
    return !this.flyoutTarget.hasAttribute('hidden')
  }

  /**
   * Reset Flyout trigger buttons, so the browser doesn't think they've been pressed
   */
  clearTriggerButtonsPressed() {
    this.triggerButtonTargets.forEach(button => $(button).attr('aria-pressed', 'false'))
  }

  /**
   * Determine whether or not an interactive element is currently underneath the Flyout.
   * We'll be able to implement this functionality with the Intersection Observer API,
   * as soon as v2 gains more widespread browser support.
   * @param {HTMLElement} element
   * @returns {boolean}
   * {@link https://stackoverflow.com/questions/12066870/how-to-check-if-an-element-is-overlapping-other-elements}
   * {@link https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect}
   * {@link https://web.dev/intersectionobserver-v2/}
   * {@link https://caniuse.com/intersectionobserver-v2}
   */
  isUnderneathFlyout(element) {
    // Grab the Flyout and element dimensions.
    const flyoutDOMRect = this.flyoutTarget.getBoundingClientRect()
    const elementDOMRect = element.getBoundingClientRect()
    // Grab the overlap "threshold," if there is one.
    // This is to allow for partial overlap, e.g., 75% overlap, 50% overlap, etc.
    // If no threshold is provided, we'll just use the full width.
    const elementOverlapThreshold = $(element).data('flyoutOverlapThreshold')
    const elementOverlapPosition = elementOverlapThreshold
      ? elementDOMRect.left + (elementDOMRect.width * elementOverlapThreshold)
      : elementDOMRect.right
    // Tell us if the Flyout is overlapping, or not!
    return !(flyoutDOMRect.left > elementOverlapPosition)
  }

  /**
   * Ensure interactive elements are unavailable to keyboard navigators and assistive tech users,
   * if those elements are currently being covered by the Flyout.
   */
  toggleInteractiveElements() {
    if (this.hasCoveredElementTarget) {
      this.coveredElementTargets.forEach(element => {
        const hidden = this.flyoutIsOpen() && this.isUnderneathFlyout(element)
        const tabindex = hidden ? '-1' : '0'
        $(element).attr({
          'aria-hidden': hidden,
          tabindex: tabindex
        })
      })
    }
  }
}
